/**
 * Status Badge Component
 * Animated status pills with pulse effect
 */

import React from 'react';
import { Chip, styled, keyframes } from '@mui/material';
import { colors } from '../../theme/colors';

type StatusType = 'active' | 'inactive' | 'success' | 'warning' | 'error';

interface StatusBadgeProps {
  status: StatusType;
  label: string;
  animated?: boolean;
}

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(32, 227, 178, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(32, 227, 178, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(32, 227, 178, 0);
  }
`;

const StyledChip = styled(Chip)<{ statustype: StatusType; animated?: boolean }>(({ statustype, animated }) => {
  let backgroundColor: string;
  let color: string;
  let dotColor: string;

  switch (statustype) {
    case 'active':
    case 'success':
      backgroundColor = 'rgba(32, 227, 178, 0.1)';
      color = colors.success.dark;
      dotColor = colors.success.main;
      break;
    case 'inactive':
      backgroundColor = colors.neutral[100];
      color = colors.neutral[600];
      dotColor = colors.neutral[400];
      break;
    case 'warning':
      backgroundColor = 'rgba(255, 184, 0, 0.1)';
      color = colors.warning.dark;
      dotColor = colors.warning.main;
      break;
    case 'error':
      backgroundColor = 'rgba(255, 107, 107, 0.1)';
      color = colors.error.dark;
      dotColor = colors.error.main;
      break;
    default:
      backgroundColor = colors.neutral[100];
      color = colors.neutral[600];
      dotColor = colors.neutral[400];
  }

  return {
    backgroundColor,
    color,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 28,
    padding: '0 12px',
    '& .MuiChip-icon': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: dotColor,
      marginLeft: 0,
      marginRight: 6,
      animation: animated && statustype === 'active' ? `${pulse} 2s infinite` : 'none',
    },
  };
});

const StatusDot = styled('span')({
  display: 'inline-block',
  width: 8,
  height: 8,
});

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label, animated = true }) => {
  return <StyledChip statustype={status} animated={animated} icon={<StatusDot />} label={label} />;
};
